import { Flex } from "antd";
import { FC } from "react";

import { NetworkIndicator } from "@/features/NetworkIndicator";
import {
    QueueTaskSnapshot,
    QueueTaskStatus,
} from "@/hooks/core/useAsyncTaskQueue";
import { PrimaryText } from "@/shared/ui/Typography";

import {
    StyledCheckCircleOutlined,
    StyledClockCircleOutlined,
    StyledContentContainer,
    StyledExclamationCircleOutlined,
    StyledList,
    StyledLoaderContainer,
    StyledLoadingOutlined,
    StyledNetworkIndicatorWrapper,
    StyledProgress,
    StyledTextItem,
} from "./Styled";

interface BaseProps {
    open: boolean;
    tasks: QueueTaskSnapshot[];
    title?: string;
}

interface SuccessiveProps extends BaseProps {
    successively: true;
    completedCount: number;
    totalCount: number;
}

interface ParallelProps extends BaseProps {
    successively?: false | undefined;
    completedCount?: never;
    totalCount?: never;
}

type ProgressLoaderProps = SuccessiveProps | ParallelProps;

const getStatusIcon = (status: QueueTaskStatus) => {
    switch (status) {
        case QueueTaskStatus.Completed:
            return <StyledCheckCircleOutlined />;
        case QueueTaskStatus.Error:
            return <StyledExclamationCircleOutlined />;
        case QueueTaskStatus.Running:
            return <StyledLoadingOutlined />;
        case QueueTaskStatus.Pending:
        default:
            return <StyledClockCircleOutlined />;
    }
};

export const ProgressLoader: FC<ProgressLoaderProps> = ({
    open,
    tasks,
    successively,
    completedCount,
    totalCount,
    title = "Підготовка даних…",
}) => {
    if (!open) return null;

    const photoTasks = tasks.filter((t) => t.label === "photo");
    const photosCompleted = photoTasks.filter(
        (t) =>
            t.status !== QueueTaskStatus.Pending &&
            t.status !== QueueTaskStatus.Running,
    );
    const restTasks = tasks.filter((t) => t.label !== "photo");

    return (
        <StyledLoaderContainer>
            <StyledNetworkIndicatorWrapper>
                <NetworkIndicator />
            </StyledNetworkIndicatorWrapper>

            <StyledContentContainer>
                <PrimaryText textColor="white" fontSize={18}>
                    {title}
                </PrimaryText>

                <Flex vertical align="center" gap="2px">
                    <PrimaryText textColor="white">
                        Не вимикайте екран
                    </PrimaryText>
                    <PrimaryText textColor="white">
                        Не закривайте застосунок
                    </PrimaryText>
                    <PrimaryText textColor="white">
                        Не оновлюйте сторінку
                    </PrimaryText>
                </Flex>

                {tasks.length > 0 &&
                    (successively ? (
                        <>
                            <StyledProgress
                                percent={Math.ceil(
                                    (completedCount / totalCount) * 100,
                                )}
                                status="active"
                            />
                            {tasks.map(
                                ({ status, id, label }) =>
                                    status === QueueTaskStatus.Running && (
                                        <StyledTextItem
                                            textColor="white"
                                            key={id}
                                        >
                                            {label}
                                        </StyledTextItem>
                                    ),
                            )}
                        </>
                    ) : (
                        <Flex vertical style={{ width: "100%" }} gap={8}>
                            {photoTasks.length > 0 && (
                                <Flex vertical gap={8}>
                                    <StyledProgress
                                        percent={Math.ceil(
                                            (photosCompleted.length /
                                                photoTasks.length) *
                                                100,
                                        )}
                                        status="active"
                                    />

                                    <StyledTextItem textColor="white">
                                        Завантажено {photosCompleted.length}{" "}
                                        фото із {photoTasks.length}
                                    </StyledTextItem>
                                </Flex>
                            )}

                            <StyledList
                                dataSource={restTasks}
                                renderItem={(task: QueueTaskSnapshot) => (
                                    <StyledList.Item>
                                        <StyledList.Item.Meta
                                            avatar={getStatusIcon(task.status)}
                                            title={
                                                <StyledTextItem
                                                    textColor="white"
                                                    fontSize={14}
                                                    fontWeight="400"
                                                >
                                                    {task.label}
                                                </StyledTextItem>
                                            }
                                        />
                                    </StyledList.Item>
                                )}
                            />
                        </Flex>
                    ))}
            </StyledContentContainer>
        </StyledLoaderContainer>
    );
};
